import { useCallback, useMemo } from 'react';

import { PaymentMethodProvider } from '@sorare/core/src/__generated__/globalTypes';
import {
  Sum,
  useMonetaryAmount,
} from '@sorare/core/src/hooks/useMonetaryAmount';
import { MonetaryAmount } from '@sorare/core/src/lib/monetaryAmount';

type Props = {
  isFiat: boolean;
  monetaryAmount: MonetaryAmount;
  creditCardFee: number;
  activeFee: boolean;
  isCreditCard?: boolean;
  potentialDiscount?: MonetaryAmount;
  cardPaymentProvider?: PaymentMethodProvider;
};
const STRIPE_MINIMUM_AMOUNT = 50;

export const useCalculateAmounts = ({
  isFiat,
  monetaryAmount,
  creditCardFee,
  activeFee,
  isCreditCard,
  potentialDiscount = MonetaryAmount.zero,
  cardPaymentProvider,
}: Props) => {
  const { usd } = useMonetaryAmount();

  const absoluteConversionCreditMonetaryAmount = potentialDiscount;
  const conversionCreditMonetaryAmount =
    absoluteConversionCreditMonetaryAmount.multipiedBy(-1);

  const feesBasisPoints = useMemo(
    () => (activeFee ? creditCardFee : 0),
    [activeFee, creditCardFee]
  );

  const feesMonetaryAmount: MonetaryAmount = useMemo(() => {
    return monetaryAmount.calculateFees(BigInt(feesBasisPoints) / 100n)
      .feesMonetaryAmount;
  }, [monetaryAmount, feesBasisPoints]);

  const totalMonetaryAmount = useMemo(() => {
    return Sum(
      isFiat
        ? [monetaryAmount, conversionCreditMonetaryAmount, feesMonetaryAmount]
        : [monetaryAmount, conversionCreditMonetaryAmount]
    );
  }, [
    isFiat,
    monetaryAmount,
    conversionCreditMonetaryAmount,
    feesMonetaryAmount,
  ]);

  const payWithConversionCreditsOnly =
    totalMonetaryAmount.isZero && conversionCreditMonetaryAmount.wei < 0n;

  // For stripe, we cannot charge less than 0.5$. The 2 following hooks are used to display this minimum if needed
  const isBellowStripeMinimum =
    !payWithConversionCreditsOnly &&
    isCreditCard &&
    cardPaymentProvider === PaymentMethodProvider.STRIPE &&
    totalMonetaryAmount.usdCents < STRIPE_MINIMUM_AMOUNT;

  const actualTotalMonetaryAmount = useMemo(() => {
    if (isBellowStripeMinimum) {
      return usd(STRIPE_MINIMUM_AMOUNT);
    }
    return totalMonetaryAmount;
  }, [totalMonetaryAmount, isBellowStripeMinimum, usd]);

  const actualConversionCreditMonetaryAmount = useCallback(
    (ma: MonetaryAmount) => {
      if (isBellowStripeMinimum) {
        return usd(STRIPE_MINIMUM_AMOUNT - monetaryAmount.usdCents);
      }
      return ma;
    },
    [monetaryAmount.usdCents, isBellowStripeMinimum, usd]
  );

  return {
    fees: feesBasisPoints / 10000,
    feesMonetaryAmount,
    conversionCreditMonetaryAmount: actualConversionCreditMonetaryAmount(
      conversionCreditMonetaryAmount
    ),
    absoluteConversionCreditMonetaryAmount:
      actualConversionCreditMonetaryAmount(
        absoluteConversionCreditMonetaryAmount
      ),
    maxDiscountMonetary: potentialDiscount,
    totalMonetaryAmount: actualTotalMonetaryAmount,
    initialAmountIsBellowStripeMinimum: isBellowStripeMinimum,
    payWithConversionCreditsOnly,
  };
};
